import React, { useEffect, useState } from "react";
import { Button, message, Popconfirm, Space, Tag } from "antd";
import { CheckCircleFilled, FormOutlined } from "@ant-design/icons";
import { useAuth } from "../contexts/AuthContext";
import useCurrentUserData from "../hooks/useCurrentUserData";
import {
  CancelarInscripcion,
  InscripcionColeccion,
  Inscribirse,
  SubscribeMiInscripcion,
} from "../inscripciones";

// ══════════════════════════════════════════════════════════════
// Botón "Inscribirme" (agregado 11/10/2026)
//
// Se muestra en un evento o noticia que tenga la inscripción
// activada desde Admin. Al tocarlo guarda nombre, email y uid del
// usuario en la subcolección Inscriptos (ver src/inscripciones.ts).
// Si ya está inscripto, muestra "Estás inscripto" y la opción de
// cancelar.
// ══════════════════════════════════════════════════════════════

interface InscripcionButtonProps {
  coleccion: InscripcionColeccion;
  itemId: string;
  /** Versión más chica, para el carrusel de noticias. */
  compact?: boolean;
}

const InscripcionButton: React.FC<InscripcionButtonProps> = ({
  coleccion,
  itemId,
  compact = false,
}) => {
  const { user } = useAuth();
  const { userData } = useCurrentUserData();
  // null = todavía no sabemos si está inscripto.
  const [inscripto, setInscripto] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.uid || !itemId) {
      setInscripto(null);
      return;
    }
    return SubscribeMiInscripcion(coleccion, itemId, user.uid, setInscripto);
  }, [coleccion, itemId, user?.uid]);

  if (!user) return null;

  const handleInscribirme = async () => {
    setSaving(true);
    try {
      await Inscribirse(coleccion, itemId, {
        uid: user.uid,
        nombre: userData?.Nombre || user.displayName || "",
        email: user.email || userData?.Email || "",
      });
      message.success("Listo, quedaste inscripto.");
    } catch {
      message.error(
        "No se pudo completar la inscripción. Probá de nuevo en un momento.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelar = async () => {
    setSaving(true);
    try {
      await CancelarInscripcion(coleccion, itemId, user.uid);
      message.success("Cancelaste tu inscripción.");
    } catch {
      message.error("No se pudo cancelar la inscripción.");
    } finally {
      setSaving(false);
    }
  };

  if (inscripto) {
    return (
      <Space wrap size={4} align="center">
        <Tag
          color="success"
          icon={<CheckCircleFilled />}
          style={{
            margin: 0,
            padding: compact ? "2px 10px" : "4px 12px",
            fontSize: compact ? 13 : 14,
            fontWeight: 700,
            borderRadius: 999,
          }}
        >
          Estás inscripto
        </Tag>
        <Popconfirm
          title="¿Cancelar tu inscripción?"
          okText="Sí, cancelar"
          okButtonProps={{ danger: true }}
          cancelText="No"
          onConfirm={() => void handleCancelar()}
        >
          <Button type="link" size="small" danger loading={saving}>
            Cancelar inscripción
          </Button>
        </Popconfirm>
      </Space>
    );
  }

  return (
    <Button
      type="primary"
      icon={<FormOutlined />}
      size={compact ? "middle" : "large"}
      loading={saving || inscripto === null}
      onClick={() => void handleInscribirme()}
      style={{
        borderRadius: 999,
        fontWeight: 700,
        background: "linear-gradient(135deg, #7c5cfc 0%, #9b7dfc 100%)",
        border: "none",
      }}
    >
      Inscribirme
    </Button>
  );
};

export default InscripcionButton;
